// kast-dev family file: BugReportForm; master: waypoint_wars/ui/web/src/components/BugReportForm.tsx
// Copied unchanged into every kast-dev web app. Change the master, copy it
// everywhere, and run infra/scripts/check-family-files.sh.
//
// The bug report form and the attached-logs viewer, for DOM apps. Needs the
// diagnostics family file at "@common/utils/diagnostics". The page around it
// (route, heading, back link, who sees the logs) is the app's.

import { useId, useState, useSyncExternalStore } from "react";
import {
  buildBugReport,
  deviceInfo,
  getApiLog,
  getLogBuffer,
  getNavLog,
  clearApiLog,
  clearLogBuffer,
  clearNavLog,
  MAX_DESCRIPTION_CHARS,
  SEVERITIES,
  submitBugReport,
  subscribeApiLog,
  subscribeLogBuffer,
  subscribeNavLog,
  type Severity,
} from "@common/utils/diagnostics";

export interface BugReportFormClassNames {
  button?: string;
  primaryButton?: string;
  /** The textarea and the severity select. */
  input?: string;
  label?: string;
  /** Small print: the attachment note, the result, the sign-in note. */
  hint?: string;
}

interface FormProps {
  /** The app's id in the inbox, e.g. "waypoint_wars". */
  app: string;
  /** Who is reporting, or null when signed out. */
  user: { id: string | null; email: string | null } | null;
  /** The app's current kast-dev access token. The inbox refuses a report without one. */
  getToken: () => string | null | undefined | Promise<string | null | undefined>;
  /** Which API the app is pointed at ("prod", "dev"), when it can switch. */
  env?: string | null;
  /** Offered as a Sign In button when there is no session. */
  onSignIn?: () => void;
  /** Pre-filled text, e.g. the crash an ErrorBoundary handed over. */
  initialDescription?: string;
  /** After the inbox accepted the report. */
  onSubmitted?: (reportId: string) => void;
  classNames?: BugReportFormClassNames;
}

const ERROR = "var(--diag-error, #e5484d)";
const WARN = "var(--diag-warn, #d4a017)";
const OK = "var(--diag-ok, #30a46c)";
const DIM = { opacity: 0.65 };

/**
 * Describe the bug; the API, console and navigation logs ride along, redacted
 * and capped by buildBugReport. Download works signed out, so a report is never
 * lost to a missing session or a dead network.
 */
export default function BugReportForm({
  app,
  user,
  getToken,
  env = null,
  onSignIn,
  initialDescription = "",
  onSubmitted,
  classNames: cn = {},
}: FormProps) {
  const id = useId();
  const [description, setDescription] = useState(initialDescription);
  const [severity, setSeverity] = useState<Severity>("medium");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const collect = () =>
    buildBugReport({ app, description: description.trim(), severity, env, user, device: deviceInfo() });

  const submit = async () => {
    if (!description.trim()) {
      setResult({ ok: false, message: "Please describe what went wrong." });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const reportId = await submitBugReport(collect(), { token: await getToken() });
      setResult({ ok: true, message: "Thank you — the report and its logs were submitted." });
      setDescription("");
      onSubmitted?.(reportId);
    } catch (err: unknown) {
      setResult({ ok: false, message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(collect(), null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `bug-report-${app}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <label className={cn.label} htmlFor={`${id}-description`} style={{ display: "block" }}>
        What went wrong?
      </label>
      <textarea
        id={`${id}-description`}
        className={cn.input}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What were you doing, and what happened instead?"
        rows={5}
        maxLength={MAX_DESCRIPTION_CHARS}
        style={{ width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
      />
      <p className={cn.hint} style={{ margin: "4px 0 0" }}>
        Recent API calls, console output and the screens you visited are attached automatically. Sign-in tokens
        are stripped before anything is sent.
      </p>

      <label className={cn.label} htmlFor={`${id}-severity`} style={{ display: "block", marginTop: 12 }}>
        Severity
      </label>
      <select
        id={`${id}-severity`}
        className={cn.input}
        value={severity}
        onChange={(e) => setSeverity(e.target.value as Severity)}
      >
        {SEVERITIES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" className={cn.primaryButton ?? cn.button} disabled={submitting} onClick={submit}>
          {submitting ? "Sending..." : "Send Report"}
        </button>
        <button type="button" className={cn.button} onClick={download}>
          Download
        </button>
      </div>

      {result && (
        <p className={cn.hint} role="status" style={{ color: result.ok ? OK : ERROR, marginTop: 10 }}>
          {result.message}
        </p>
      )}
      {!user && (
        <div style={{ marginTop: 10 }}>
          <p className={cn.hint} style={{ margin: 0 }}>
            Sending requires signing in. You can download the report and send it on instead.
          </p>
          {onSignIn && (
            <button type="button" className={cn.button} style={{ marginTop: 8 }} onClick={onSignIn}>
              Sign In
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type Tab = "api" | "console" | "nav";

const row = { fontSize: 11, fontFamily: "monospace", padding: "3px 0" } as const;
const time = (ts: number) => new Date(ts).toLocaleTimeString();

/**
 * The three logs as they stand, for someone who wants to look before sending.
 * Matters most in a production build, where the logger records but prints
 * nothing, so a failed request leaves no trace in the browser's devtools.
 */
export function DiagnosticLogs({ classNames: cn = {} }: { classNames?: Pick<BugReportFormClassNames, "button" | "primaryButton"> }) {
  const [tab, setTab] = useState<Tab>("api");
  const apiLog = useSyncExternalStore(subscribeApiLog, getApiLog);
  const consoleLog = useSyncExternalStore(subscribeLogBuffer, getLogBuffer);
  const navLog = useSyncExternalStore(subscribeNavLog, getNavLog);

  const tabs: { id: Tab; label: string }[] = [
    { id: "api", label: `API (${apiLog.length})` },
    { id: "console", label: `Console (${consoleLog.length})` },
    { id: "nav", label: `Screens (${navLog.length})` },
  ];
  const clear = { api: clearApiLog, console: clearLogBuffer, nav: clearNavLog }[tab];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-pressed={tab === t.id}
            className={tab === t.id ? (cn.primaryButton ?? cn.button) : cn.button}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className={cn.button} style={{ marginLeft: "auto" }} onClick={clear}>
          Clear
        </button>
      </div>

      <div style={{ maxHeight: 260, overflow: "auto" }}>
        {tab === "api" &&
          (apiLog.length === 0 ? (
            <span style={DIM}>No API calls recorded yet.</span>
          ) : (
            apiLog.map((e) => (
              <div key={e.id} style={{ ...row, whiteSpace: "nowrap" }}>
                <span style={DIM}>{time(e.timestamp)}</span> <span style={{ fontWeight: 700 }}>{e.method}</span>{" "}
                {e.path} <span style={{ color: e.error ? ERROR : OK }}>{e.status ?? "ERR"}</span>{" "}
                <span style={DIM}>{e.durationMs}ms</span>
                {e.error && e.error !== String(e.status) && <span style={{ color: ERROR }}> {e.error}</span>}
              </div>
            ))
          ))}
        {tab === "console" &&
          (consoleLog.length === 0 ? (
            <span style={DIM}>No console output recorded yet.</span>
          ) : (
            consoleLog.map((e) => (
              <div key={e.id} style={row}>
                <span style={DIM}>{time(e.timestamp)}</span>{" "}
                <span style={e.level === "error" ? { color: ERROR } : e.level === "warn" ? { color: WARN } : DIM}>
                  {e.level.toUpperCase()}
                </span>{" "}
                {e.message}
              </div>
            ))
          ))}
        {tab === "nav" &&
          (navLog.length === 0 ? (
            <span style={DIM}>No screens recorded yet.</span>
          ) : (
            navLog.map((e) => (
              <div key={e.id} style={row}>
                <span style={DIM}>{time(e.timestamp)}</span> {e.screen}
              </div>
            ))
          ))}
      </div>
    </div>
  );
}
